import type { InviteView } from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { Input } from "@screener/uikit/components/input";
import { LoadingRegion } from "@screener/uikit/components/loading-skeleton";
import { Skeleton } from "@screener/uikit/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@screener/uikit/components/table";
import { useState } from "react";
import { formatDateTime } from "@/density/format";
import { copyText } from "@/lib/clipboard";
import { SIGNUP_HASH } from "@/lib/signup-route";

/**
 * Содержимое окна приглашений (§104 п. 6, spec `web-signup`): выдача ссылки,
 * поле только для чтения с «Скопировать» и таблица своих ссылок. Часть
 * экрана, не UIKit: знает про `InviteView`. Состояние и запросы держит
 * `useInvites`, здесь только разметка.
 */

export const INVITES_MENU_LABEL = "Пригласить";
export const INVITES_CREATE = "Создать ссылку";
export const INVITES_COPY = "Скопировать";
export const INVITES_REVOKE = "Отозвать";
export const INVITES_EMPTY = "Выданных ссылок пока нет.";
export const INVITES_LOADING = "Читаем список ссылок…";
export const INVITES_RETRY = "Повторить";
export const INVITES_LOAD_FAILED =
  "Список ссылок прочитать не удалось — это не значит, что он пуст.";
export const INVITES_FORBIDDEN = "У вас нет права приглашать, либо приглашения сейчас выключены.";
export const INVITES_LINK_LABEL = "Ссылка-приглашение";
const COPY_FAILED = "Не удалось скопировать — выделите ссылку и скопируйте вручную.";
const COPIED = "Ссылка скопирована.";

/**
 * Ссылка-приглашение: токен в хеше адреса не уходит на сервер и в журналы
 * прокси (design.md Д4). Хеш-маршрут тот же, что разбирает `signup-route.ts`.
 * Основа — `origin` + `pathname` страницы, а не один origin: приложение, розданное
 * не с корня, открывается по ссылке там же.
 */
export function inviteLink(base: string, token: string): string {
  return `${base}${SIGNUP_HASH}/${token}`;
}

/** Основа ссылки: адрес страницы без query и хеша, с завершающим слешем пути. */
export function inviteLinkBase(location: Pick<Location, "origin" | "pathname">): string {
  return `${location.origin}${location.pathname}`;
}

/** Итог копирования, привязанный к ссылке, которую копировали. */
export interface CopyNotice {
  link: string;
  text: string;
}

/**
 * «Ссылка скопирована» относится к той ссылке, что копировали: с выдачей
 * новой оно не показывается, пока не скопирована уже она.
 */
export function visibleCopyNotice(
  notice: CopyNotice | null,
  createdLink: string | null,
): string | null {
  return notice !== null && notice.link === createdLink ? notice.text : null;
}

/** Статус приглашения словами; у использованной — логин заведённой записи. */
export function inviteStatusLabel(invite: InviteView): string {
  switch (invite.status) {
    case "pending":
      return "Ждёт";
    case "used":
      return invite.used_by_login === null
        ? "Использована"
        : `Использована — ${invite.used_by_login}`;
    case "expired":
      return "Истекла";
    case "revoked":
      return "Отозвана";
    default:
      return invite.status satisfies never;
  }
}

export type InvitesListState =
  | { status: "loading" }
  | { status: "ready"; invites: InviteView[] }
  | { status: "unavailable" }
  | { status: "forbidden" };

export interface InvitesPanelProps {
  state: InvitesListState;
  /** Полная ссылка только что выданного приглашения; `null` — ничего не выдано. */
  createdLink: string | null;
  creating: boolean;
  /** Приглашения, чей отзыв ещё в пути. */
  pendingIds: ReadonlySet<string>;
  /** Неудача последнего действия, словами. */
  failure: string | null;
  onCreate: () => void;
  onRevoke: (id: string) => void;
  onReload: () => void;
}

export function InvitesPanel({
  state,
  createdLink,
  creating,
  pendingIds,
  failure,
  onCreate,
  onRevoke,
  onReload,
}: InvitesPanelProps) {
  const [copyNotice, setCopyNotice] = useState<CopyNotice | null>(null);

  const shownCopyNotice = visibleCopyNotice(copyNotice, createdLink);

  async function copy(link: string): Promise<void> {
    const outcome = await copyText(link, navigator.clipboard);
    setCopyNotice({ link, text: outcome === "copied" ? COPIED : COPY_FAILED });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div>
          <Button size="sm" onClick={onCreate} disabled={creating || state.status === "forbidden"}>
            {INVITES_CREATE}
          </Button>
        </div>
        {createdLink !== null ? (
          <div className="flex items-center gap-2">
            {/* Правило: ссылка с токеном выводится только в поле ввода — запись
                сессии маскирует его (maskAllInputs). Текстом её не показывать:
                before_send не проверяет текст DOM глубже своего предела. */}
            <Input
              readOnly
              aria-label={INVITES_LINK_LABEL}
              value={createdLink}
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void copy(createdLink);
              }}
            >
              {INVITES_COPY}
            </Button>
          </div>
        ) : null}
        {shownCopyNotice !== null ? (
          <p role="status" className="text-xs text-muted-foreground">
            {shownCopyNotice}
          </p>
        ) : null}
        {failure !== null ? (
          <p role="alert" className="text-xs text-destructive">
            {failure}
          </p>
        ) : null}
      </div>
      <InvitesList state={state} pendingIds={pendingIds} onRevoke={onRevoke} onReload={onReload} />
    </div>
  );
}

function InvitesList({
  state,
  pendingIds,
  onRevoke,
  onReload,
}: Pick<InvitesPanelProps, "state" | "pendingIds" | "onRevoke" | "onReload">) {
  if (state.status === "loading") {
    return (
      <LoadingRegion label={INVITES_LOADING} className="flex flex-col gap-2">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-2/3" />
      </LoadingRegion>
    );
  }
  if (state.status === "forbidden") {
    return (
      <p role="alert" className="text-sm text-destructive">
        {INVITES_FORBIDDEN}
      </p>
    );
  }
  if (state.status === "unavailable") {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="text-sm text-destructive">
          {INVITES_LOAD_FAILED}
        </p>
        <Button size="sm" variant="outline" onClick={onReload}>
          {INVITES_RETRY}
        </Button>
      </div>
    );
  }
  if (state.invites.length === 0) {
    return <p className="text-sm text-muted-foreground">{INVITES_EMPTY}</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Выдана</TableHead>
          <TableHead>Срок</TableHead>
          <TableHead>Статус</TableHead>
          <TableHead>
            <span className="sr-only">Действие</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {state.invites.map((invite) => (
          <TableRow key={invite.id}>
            <TableCell>{formatDateTime(invite.created_at_ms)}</TableCell>
            <TableCell>{formatDateTime(invite.expires_at_ms)}</TableCell>
            <TableCell>{inviteStatusLabel(invite)}</TableCell>
            <TableCell className="text-right">
              {invite.status === "pending" ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pendingIds.has(invite.id)}
                  onClick={() => onRevoke(invite.id)}
                >
                  {INVITES_REVOKE}
                </Button>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
